import { supabase } from './supabase';

export type MathPracticeMode = 'forward_recall' | 'missing_factor' | 'division_link';

export interface MathTableProgress {
  tableNumber: number;
  totalAttempts: number;
  correctAttempts: number;
  accuracyPercent: number;
  secureFacts: number;
  masteredFacts: number;
  totalFacts: number;
  lastPractisedAt: string | null;
}

export interface MathPracticeQuestion {
  questionNumber: number;
  factId: number;
  tableNumber: number;
  multiplier: number;
  product: number;
  practiceMode: MathPracticeMode;
}

export interface MathAnswerFeedback {
  isCorrect: boolean;
  correctAnswer: number;
  explanation: string;
  currentScore: number;
}

export interface MathPracticeResult {
  sessionId: string;
  score: number;
  questionCount: number;
  percentage: number;
  durationSeconds: number;
  completedAt: string;
}

function throwIfError(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

export async function loadMathTableProgress(childId: string): Promise<MathTableProgress[]> {
  const { data, error } = await supabase.rpc('get_math_table_progress', { p_child_id: childId });
  throwIfError(error);

  return (data ?? []).map((row: Record<string, unknown>) => ({
    tableNumber: Number(row.table_number),
    totalAttempts: Number(row.total_attempts ?? 0),
    correctAttempts: Number(row.correct_attempts ?? 0),
    accuracyPercent: Number(row.accuracy_percent ?? 0),
    secureFacts: Number(row.secure_facts ?? 0),
    masteredFacts: Number(row.mastered_facts ?? 0),
    totalFacts: Number(row.total_facts ?? 12),
    lastPractisedAt: row.last_practised_at as string | null,
  }));
}

export async function startMathTablePractice(
  childId: string,
  tableNumbers: number[],
  practiceMode: MathPracticeMode,
  questionCount: number,
): Promise<string> {
  const { data, error } = await supabase.rpc('start_table_practice', {
    p_child_id: childId,
    p_table_numbers: tableNumbers,
    p_practice_mode: practiceMode,
    p_question_count: questionCount,
  });
  throwIfError(error);
  if (!data) throw new Error('The table-practice session could not be created.');
  return data as string;
}

export async function loadMathPracticeQuestions(sessionId: string): Promise<MathPracticeQuestion[]> {
  const { data, error } = await supabase.rpc('get_table_practice_questions', { p_session_id: sessionId });
  throwIfError(error);

  return (data ?? []).map((row: Record<string, unknown>) => ({
    questionNumber: Number(row.question_number),
    factId: Number(row.fact_id),
    tableNumber: Number(row.table_number),
    multiplier: Number(row.multiplier),
    product: Number(row.product),
    practiceMode: row.practice_mode as MathPracticeMode,
  }));
}

export async function submitMathPracticeAnswer(
  sessionId: string,
  questionNumber: number,
  submittedAnswer: number,
  responseMs: number,
): Promise<MathAnswerFeedback> {
  const { data, error } = await supabase.rpc('submit_table_practice_answer', {
    p_session_id: sessionId,
    p_question_number: questionNumber,
    p_submitted_answer: submittedAnswer,
    p_response_ms: Math.min(300000, Math.max(0, Math.round(responseMs))),
  });
  throwIfError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('The table answer could not be checked.');
  return {
    isCorrect: Boolean(row.is_correct),
    correctAnswer: Number(row.correct_answer),
    explanation: String(row.explanation ?? ''),
    currentScore: Number(row.current_score ?? 0),
  };
}

export async function finishMathTablePractice(sessionId: string): Promise<MathPracticeResult> {
  const { data, error } = await supabase.rpc('finish_table_practice', { p_session_id: sessionId });
  throwIfError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('The table-practice result could not be saved.');
  return {
    sessionId: row.session_id as string,
    score: Number(row.score ?? 0),
    questionCount: Number(row.question_count ?? 0),
    percentage: Number(row.percentage ?? 0),
    durationSeconds: Number(row.duration_seconds ?? 0),
    completedAt: row.completed_at as string,
  };
}
